import type { Statement, Statements } from '../interface';
import type { Statement as ResponseStatement } from '@simplywallst/services/lib/endpoints/types/company/general/CompanyAPIStatements';
import { fetchCompanyStatements } from '@company/redux/routines';
import produce from 'immer';

type Severity = 'minor' | 'major' | 'none';
type StatementValue = 'Pass' | 'Fail' | 'No data' | 'UnInitialised';
type Area =
  | 'BankHealth'
  | 'Dividends'
  | 'Future'
  | 'Health'
  | 'Management'
  | 'Misc'
  | 'Past'
  | 'Risks'
  | 'Value';

interface MapStatement {
  name: string;
  title: string;
  area: Area | null;
  type: ResponseStatement['type'];
  outcome: number;
  outcome_name: string;
  description: string;
  context: Record<string, string>;
  question: {
    data: string[];
  };
  severity: Severity;
  value: StatementValue;
  outcomeName: string;
  url: { label: string; href: string } | null;
}

const mapStatement = (statement: ResponseStatement): MapStatement => {
  const statementValueTrue: StatementValue =
    statement.value === true ? 'Pass' : 'No data';
  return {
    ...statement,
    description: statement.description || '',
    severity: statement.severity || 'none',
    context: statement.context || {},
    outcomeName: statement.outcome_name,
    value: statement.value === false ? 'Fail' : statementValueTrue,
    url: !Array.isArray(statement.url) ? statement.url : null,
  };
};

function getDefaultStatement(): Statement {
  return {
    date: null,
    context: {},
    description: '',
    severity: 'none',
    outcomeName: '',
    value: 'No data',
    title: '',
    area: null,
    type: 'statements',
    outcome: null,
    question: '',
    url: null,
  };
}

const getDefaultState = (): Record<string, Statements> => {
  return {};
};

export function statementReducer(
  state = getDefaultState(),
  action: ReturnType<typeof fetchCompanyStatements['success']>
) {
  return produce(state, (draftState) => {
    switch (action.type) {
      case fetchCompanyStatements.SUCCESS: {
        try {
          const {
            payload: { statements, companyId },
          } = action as ReturnType<typeof fetchCompanyStatements['success']>;
          statements.statements.data.forEach((statement) => {
            const safeStatement = mapStatement(statement);
            const {
              name,
              title,
              area,
              type,
              value,
              outcome,
              outcomeName,
              severity,
              description,
              context,
              question: { data: question },
              url,
            } = safeStatement;
            //
            if (!state[companyId] && !draftState[companyId]) {
              draftState[companyId] = {};
            }
            //
            if (!draftState[companyId][name]) {
              draftState[companyId][name] = getDefaultStatement();
            }
            //
            draftState[companyId][name].context = context;
            draftState[companyId][name].description = description;
            draftState[companyId][name].severity = severity;
            draftState[companyId][name].outcomeName = outcomeName;
            draftState[companyId][name].value = value;
            draftState[companyId][name].title = title;
            draftState[companyId][name].area = area;
            draftState[companyId][name].type = type;
            draftState[companyId][name].outcome = outcome;
            draftState[companyId][name].question =
              question && question[0] ? question[0] : '';
            draftState[companyId][name].url = url;
          });
        } catch (error: any) {
          console.log('Statement Reducer failed', {
            message: error.message,
            stack: error.stack,
          });
        }
        break;
      }
    }
  });
}
